import { Divider, TableCell, TableRow, Typography } from "@mui/material";
import {
  KeyValue,
  Query,
  SearchPillData,
  TableHeader,
} from "../../../../interfaces";
import makeStyles from "@mui/styles/makeStyles";
import { selectedStatesAlpha } from "../../../../refdata/FilterOptions";
import SearchPills from "../../../../containers/SearchPills";
import {
  fileNameFormat,
  sortObjValue,
} from "../../../../common/services/Utilities";
import MultiHeaderTable from "../../../MultiHeaderTable/MultiHeaderTable";
import React from "react";
import { fetchData } from "../../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../../config";
import generateBranchOfficeDepositsApiUrl from "../../BranchOfficeDeposits/BranchOfficeDepositsUtilities";
import { transformDataForDownload } from "./DepositMarketShareUtilities";
import ApiDataDownload, { apiDownloadProps } from "../../../ApiDataDownload";
import ModalDialog from "../../../ModalDialog";
import BranchOfficeDeposits from "../../../../containers/BranchOfficeDeposits";
import ShowHideButton from "../../../ShowHideButton";

interface Props {
  appState: Query;
  updateFilter: (field: string, value: string | string[]) => void;
}

const useStyles = makeStyles(theme => ({
  heading: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  totalsRow: {
    backgroundColor: theme.palette.primary.main,
  },
  countyRowStyles: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#D9E8F6",
    },
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body": {
      cursor: "pointer",
      backgroundColor: "#cbd8e5",
    },
  },
  institutionRowStyle1: {
    "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body": {
      cursor: "pointer",
      backgroundColor: "#D9E8F6",
    },
  },
  rowIndent1: {
    "& td": {
      fontWeight: "bold",
    },
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 30,
    },
  },
  rowIndent2: {
    "&.MuiTableCell-root.MuiTableCell-body": {
      textAlign: "left",
      paddingLeft: 45,
    },
  },
  tableRowStyles: {
    "& td.MuiTableCell-root.MuiTableCell-body": {
      backgroundColor: "#acc7df",
      fontWeight: "bold",
    },
  },
}));

export default function DepositMarketShareCountySummaryByState({
  appState,
  updateFilter,
}: Props) {
  const stateName: string | undefined = selectedStatesAlpha.find(
    state => state.value === appState.filters.stalpbr,
  )?.label;
  const [previousState, setPreviousState] = React.useState(
    appState.filters.stalpbr,
  );
  const classes = useStyles();
  const [fetchInstitutionsByCert, setFetchInstitutionsByCert] = React.useState(
    false,
  );
  const [fetchStateCountyData, setFetchStateCountyData] = React.useState(false);
  const [stateData, setStateData] = React.useState<KeyValue[]>([]);
  const [countyData, setCountyData] = React.useState<KeyValue[]>([]);
  const [stateCounties, setStateCounties] = React.useState<string[]>([]);
  const [institutionByCountyCert, setInstitutionByCountyCert] = React.useState<
    KeyValue
  >({});
  const [downloadData, setDownloadData] = React.useState<KeyValue[]>();
  const [countyDataObj, setCountyDataObj] = React.useState<KeyValue>({});
  const [open, setOpen] = React.useState("");
  const [name, setName] = React.useState("");
  const branchOfficeModal = (
    <ModalDialog
      title={name}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: { ...appState.filters, cert: open },
        }}
      />
    </ModalDialog>
  );
  const msrSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
    {
      label: "State",
      value: `${appState.filters.stalpbr}`,
      search: false,
      static: true,
      formKey: "stalpbr",
    },
    {
      label: "County",
      value: `${appState.filters.county}`,
      search: false,
      static: false,
      formKey: "county",
    },
  ];
  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={msrSearchPills} />}
    />
  );
  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("County_Summary_By_State")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
    },
  ];
  const headingTitle = (
    <>
      <Typography className={classes.heading}>
        County Summary Market Shares for {stateName}
      </Typography>
      <div style={{ position: "relative", right: 0, top: 5 }}>
        {downloadData && downloadData.length > 0 && (
          <ApiDataDownload
            app="SOD"
            apiData={apiData}
            downloadData={downloadData}
          />
        )}
      </div>
    </>
  );

  const headerData: KeyValue[] = [
    {
      title: `${
        appState.filters.county ? "County / Institution Name" : "State / County"
      }`,
      apiField: "",
      styles: { textAlign: "left", width: 150, minWidth: 150, paddingLeft: 30 },
    },
    {
      title: "",
      apiField: "",
      styles: { minWidth: 2, width: 2 },
    },
    {
      title: `${
        appState.filters.county
          ? "Institution Count / Cert"
          : "Institution Count"
      }`,
      apiField: "",
      styles: { minWidth: 90, width: 90 },
    },
    {
      title: "Office Count",
      apiField: "",
      styles: { minWidth: 52, width: 52 },
    },
    {
      title: "Total Deposits (Thousands USD)",
      apiField: "",
      styles: { textAlign: "right", width: 140, minWidth: 140 },
    },
    {
      title: "Market Share",
      apiField: "",
      styles: { width: 52, minWidth: 52 },
    },
  ];

  const tableHeaders: TableHeader[][] = [
    headerData.map(element => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  //This is to remove the county filter when the state changes
  React.useEffect(() => {
    if (previousState !== appState.filters.stalpbr) {
      updateFilter("county", "");
      setPreviousState(appState.filters.stalpbr);
    }
  }, [updateFilter, appState.filters.stalpbr, previousState]);

  React.useEffect(() => {
    let isMounted = true;
    setFetchStateCountyData(false);
    (async () => {
      await Promise.all([
        fetchData(
          `${ApiBaseUrl()}/sod?filters=STALPBR:"${
            appState.filters.stalpbr
          }" AND YEAR:${appState.filters.lastYear}
          &agg_by=CNTYNAMB&get_unique=CERT&agg_sum_fields=DEPSUMBR&agg_limit=10000`,
        ),
        fetchData(
          `${ApiBaseUrl()}/sod?agg_by=STALPBR&agg_sum_fields=DEPSUMBR&get_unique=CERT&filters=STALPBR:"${
            appState.filters.stalpbr
          }" AND YEAR:${appState.filters.lastYear}`,
        ),
      ])
        .then(response => {
          if (isMounted) {
            let allCounties: string[] = response[0].data.map(
              (e: KeyValue) => e.CNTYNAMB,
            );
            setStateCounties(allCounties);
            const countyMap: { [key: string]: boolean } = {};
            allCounties.forEach(county => {
              countyMap[county] = false;
            });
            setStateData(response[1].data);
            const sortedDataDesc = sortObjValue(
              response[0].data,
              "sum_DEPSUMBR",
              true,
            );
            setCountyData(sortedDataDesc);
            setFetchStateCountyData(true);
          }
        })
        .catch(error => {
          console.error(`${error.name} : ${error.message}`);
        })
        .finally(() => {
          isMounted = false;
        });
    })();
  }, [appState.filters.stalpbr, appState.filters.lastYear, updateFilter]);

  //This is to get the institution names
  React.useEffect(() => {
    if (stateCounties.length > 0 && stateCounties[0]) {
      let isMounted = true;
      setFetchInstitutionsByCert(false);
      (async () => {
        await Promise.all(
          stateCounties.map((county: string) => {
            return fetchData(
              `${ApiBaseUrl()}/sod`,
              generateBranchOfficeDepositsApiUrl(
                {
                  ...appState,
                  filters: {
                    ...appState.filters,
                    county: county,
                    stalpbr: appState.filters.stalpbr,
                  },
                },
                undefined,
                undefined,
                undefined,
                "CERT",
              ),
            );
          }),
        )
          .then(response => {
            if (isMounted) {
              const countyByCertWithName: KeyValue = {};
              let currCounty = "";
              response.forEach((resp: KeyValue) => {
                const certName: KeyValue = {};
                resp.data.forEach((item: KeyValue) => {
                  certName[item.CERT] = item.NAMEFULL;
                  currCounty = item.CNTYNAMB;
                });
                countyByCertWithName[currCounty] = certName;
              });
              setInstitutionByCountyCert(countyByCertWithName);
              setFetchInstitutionsByCert(true);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [appState, stateCounties]);

  React.useEffect(() => {
    if (
      stateCounties.length > 0 &&
      stateCounties[0] &&
      fetchInstitutionsByCert &&
      fetchStateCountyData
    ) {
      let isMounted = true;
      let countyArr: string[] = [];
      (async () => {
        await Promise.all(
          stateCounties.map((county: string) => {
            countyArr.push(county);
            return fetchData(
              `${ApiBaseUrl()}/sod`,
              generateBranchOfficeDepositsApiUrl(
                {
                  ...appState,
                  filters: {
                    ...appState.filters,
                    county: county,
                    stalpbr: appState.filters.stalpbr,
                  },
                },
                { by: "CERT", limit: 10000, sum: ["DEPSUMBR"] },
                undefined,
                undefined,
                "CERT",
                undefined,
                "DEPSUMBR",
                "desc",
              ),
            );
          }),
        )
          .then(response => {
            if (isMounted && response) {
              const countyDataObj: KeyValue = {};
              response.forEach((resp, countyIndex) => {
                const currentCounty = countyArr[countyIndex];
                countyDataObj[currentCounty] = resp.data;
              });
              setCountyDataObj(countyDataObj);
              Object.keys(institutionByCountyCert).length > 0 &&
                countyData &&
                stateData &&
                setDownloadData(
                  transformDataForDownload(
                    countyDataObj,
                    appState.filters.lastYear,
                    countyData,
                    stateName,
                    institutionByCountyCert,
                    stateData[0],
                    appState.filters.county,
                  ),
                );
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    appState,
    stateCounties,
    institutionByCountyCert,
    stateData,
    stateName,
    countyData,
    fetchInstitutionsByCert,
    fetchStateCountyData,
  ]);
  const stateRows = (data: KeyValue) => {
    return (
      <React.Fragment key={`stateRow_${stateName}`}>
        <TableRow
          id={`stateRow`}
          key={`stateRow`}
          className={classes.tableRowStyles}
          tabIndex={0}
        >
          <TableCell className={classes.rowIndent1}>{stateName}</TableCell>
          <TableCell style={{ padding: 0 }}></TableCell>
          <TableCell>
            {data.unique_count ? data.unique_count.toLocaleString() : 0}
          </TableCell>
          <TableCell>{data.count ? data.count.toLocaleString() : 0}</TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR ? data.sum_DEPSUMBR.toLocaleString() : 0}
          </TableCell>
          <TableCell>100.00%</TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const stateTotal: number = stateData?.[0]?.["sum_DEPSUMBR"];

  const showModal = (cert: string, name: string) => {
    if (
      appState.filters.stalpbr &&
      appState.filters.county &&
      appState.filters.lastYear
    ) {
      setOpen(cert);
      setName(name);
    }
  };

  let institutionData: KeyValue[] = [{}];
  let rows: JSX.Element[];

  const institutionRow = (county: string, countyTotal: number) => {
    if (
      institutionData &&
      institutionData.length > 0 &&
      institutionByCountyCert &&
      institutionByCountyCert.hasOwnProperty(county) &&
      countyDataObj &&
      countyDataObj.hasOwnProperty(county)
    ) {
      institutionData = countyDataObj[county];
      rows = institutionData.map((data: KeyValue, int: number) => (
        <React.Fragment key={`${data.CERT}_${int}`}>
          <TableRow
            id={`${data.CERT}_${int}_row`}
            key={`${data.CERT}_${int}_row`}
            style={
              int % 2 !== 0 ? { backgroundColor: "rgba(0, 0, 0, 0.03)" } : {}
            }
            className={classes.institutionRowStyle1}
            onClick={() =>
              showModal(data.CERT, institutionByCountyCert[county][data.CERT])
            }
          >
            <TableCell
              style={{
                textAlign: "left",
                paddingLeft: 45,
              }}
            >
              {institutionByCountyCert[county][data.CERT]}
            </TableCell>
            <TableCell />
            <TableCell>{data.CERT}</TableCell>
            <TableCell
              style={{ color: "#0052CC", textDecoration: "underline" }}
            >
              {data.count}
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              {data.sum_DEPSUMBR?.toLocaleString()}
            </TableCell>
            <TableCell>
              {((data.sum_DEPSUMBR / countyTotal) * 100).toFixed(2)}%
            </TableCell>
          </TableRow>
        </React.Fragment>
      ));
    }
    return rows;
  };

  const filterByCounty = (county: string) => {
    updateFilter("county", county);
  };

  const countyRows = countyData.map((data, int) => {
    let county: string = appState.filters.county;

    return county && data.CNTYNAMB !== county ? null : (
      <React.Fragment key={data.CNTYNAMB}>
        <TableRow
          id={`${data.CNTYNAMB}_${int}`}
          key={`${data.CNTYNAMB}_${int}`}
          className={county ? classes.rowIndent1 : classes.countyRowStyles}
          style={county ? { backgroundColor: "#acc7df" } : {}}
          onClick={() => filterByCounty(data.CNTYNAMB)}
        >
          <TableCell
            className={county ? classes.rowIndent1 : classes.rowIndent2}
          >
            {data.CNTYNAMB}
          </TableCell>
          <TableCell />
          <TableCell>
            {data.unique_count ? data.unique_count.toLocaleString() : 0}
          </TableCell>
          <TableCell
            style={
              !county ? { color: "#0052CC", textDecoration: "underline" } : {}
            }
          >
            {data.count ? data.count.toLocaleString() : 0}
          </TableCell>
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR ? data.sum_DEPSUMBR.toLocaleString() : 0}
          </TableCell>
          <TableCell>
            {appState.filters.county
              ? `100%`
              : `${((data.sum_DEPSUMBR / stateTotal) * 100).toFixed(2)}%`}
          </TableCell>
        </TableRow>
        {appState.filters.county &&
        countyDataObj &&
        Object.keys(countyDataObj).length > 0
          ? institutionRow(data.CNTYNAMB, data.sum_DEPSUMBR)
          : null}
      </React.Fragment>
    );
  });

  const totalsRow = (data: KeyValue, county: string) => {
    let filteredData: KeyValue = {};
    if (county) {
      filteredData = data.filter((item: KeyValue) => item.CNTYNAMB === county);
    }
    const totalRow = (
      <React.Fragment key={`totals`}>
        <TableRow
          id={`totalsRow`}
          className={classes.totalsRow}
          style={{ backgroundColor: "#1A4480" }}
          tabIndex={0}
        >
          <TableCell
            style={{ color: "#ffffff", fontWeight: "bold" }}
            className={classes.rowIndent1}
          >
            Totals
          </TableCell>
          <TableCell />
          <TableCell style={{ color: "#ffffff", fontWeight: "bold" }}>
            {county && filteredData.length > 0
              ? filteredData[0].unique_count?.toLocaleString()
              : data.unique_count?.toLocaleString()}
          </TableCell>
          <TableCell style={{ color: "#ffffff", fontWeight: "bold" }}>
            {county && filteredData.length > 0
              ? filteredData[0].count?.toLocaleString()
              : data.count?.toLocaleString()}
          </TableCell>
          <TableCell
            style={{ color: "#ffffff", textAlign: "right", fontWeight: "bold" }}
          >
            {county && filteredData.length > 0
              ? filteredData[0].sum_DEPSUMBR?.toLocaleString()
              : data.sum_DEPSUMBR?.toLocaleString()}
          </TableCell>
          <TableCell style={{ color: "#ffffff", fontWeight: "bold" }}>
            100.00%
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
    return totalRow;
  };

  const body = (
    <React.Fragment key={appState.filters.stalpbr}>
      {stateData &&
      Object.keys(stateData).length > 0 &&
      stateData[0] &&
      !appState.filters.county
        ? stateRows(stateData[0])
        : null}
      {countyRows}
      {!appState.filters.county &&
      stateData &&
      Object.keys(stateData).length > 0 &&
      stateData[0]
        ? totalsRow(stateData[0], appState.filters.county)
        : null}
      {appState.filters.county
        ? totalsRow(countyData, appState.filters.county)
        : null}
    </React.Fragment>
  );

  return (
    <>
      {headingTitle}
      <Divider />
      <>
        {searchPills}
        {branchOfficeModal}
        <MultiHeaderTable tableBody={<>{body}</>} tableHeaders={tableHeaders} />
      </>
    </>
  );
}
